import React, { useState } from 'react';
import { ChevronDown, ChevronRight, User, Clock, AlertCircle, CheckCircle, XCircle, Info, Eye, EyeOff } from 'lucide-react';
import { maskAllPII, detectPII, MASKING_LEVELS } from '../../utils/dataPrivacy';

const LogEntry = ({ log, expanded = false, onToggleExpanded }) => {
  // Use external expansion state if provided, otherwise fall back to internal state
  const [internalExpanded, setInternalExpanded] = useState(false);
  const [showRawData, setShowRawData] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [maskingLevel, setMaskingLevel] = useState(MASKING_LEVELS.PARTIAL);

  // Use external expanded state if provided, otherwise use internal
  const isExpanded = onToggleExpanded ? expanded : internalExpanded;
  const toggleExpanded = onToggleExpanded || (() => setInternalExpanded(!internalExpanded));

  const getSeverityIcon = (severity) => {
    switch (severity) {
      case 'LOW':
        return <Info className="h-4 w-4 text-blue-500" />;
      case 'MEDIUM':
        return <AlertCircle className="h-4 w-4 text-yellow-500" />;
      case 'HIGH':
        return <AlertCircle className="h-4 w-4 text-orange-500" />;
      case 'CRITICAL':
        return <XCircle className="h-4 w-4 text-red-500" />;
      default:
        return <CheckCircle className="h-4 w-4 text-green-500" />;
    }
  };

  const getSeverityBadgeColor = (severity) => {
    switch (severity) {
      case 'LOW':
        return 'bg-blue-100 text-blue-800';
      case 'MEDIUM':
        return 'bg-yellow-100 text-yellow-800';
      case 'HIGH':
        return 'bg-orange-100 text-orange-800';
      case 'CRITICAL':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-green-100 text-green-800';
    }
  };

  const getActionIcon = (action) => {
    switch (action) {
      case 'CREATE':
        return '➕';
      case 'UPDATE':
        return '✏️';
      case 'DELETE':
        return '🗑️';
      case 'VIEW':
        return '👁️';
      case 'EXPORT':
        return '📤';
      case 'BULK_UPDATE':
        return '📊';
      default:
        return '📝';
    }
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return 'Unknown time';
    
    let date;
    if (timestamp.seconds) {
      // Firestore Timestamp
      date = new Date(timestamp.seconds * 1000);
    } else {
      date = new Date(timestamp);
    }
    
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    }).format(date);
  };

  const formatUserInfo = (log) => {
    if (log.userEmail) {
      return log.userEmail;
    }
    if (log.userId) {
      return `User ID: ${log.userId.substring(0, 8)}...`;
    }
    return 'System';
  };

  const formatDescription = (log) => {
    if (log.description) {
      return log.description;
    }

    // Generate description based on action and entity
    switch (log.action) {
      case 'CREATE':
        return `Created ${log.entityType} record${log.entityId ? ` (ID: ${log.entityId})` : ''}`;
      case 'UPDATE':
        return `Updated ${log.entityType} record${log.entityId ? ` (ID: ${log.entityId})` : ''}`;
      case 'DELETE':
        return `Deleted ${log.entityType} record${log.entityId ? ` (ID: ${log.entityId})` : ''}`;
      case 'VIEW':
        return `Viewed ${log.entityType} record${log.entityId ? ` (ID: ${log.entityId})` : ''}`;
      case 'EXPORT':
        return `Exported ${log.entityType} data`;
      case 'BULK_UPDATE':
        return `Bulk updated ${log.entityType} records`;
      default:
        return `${log.action} operation on ${log.entityType}`;
    }
  };

  const formatChanges = (changes) => {
    if (!changes || typeof changes !== 'object') return null;

    const { before, after, fieldsChanged } = changes;
    
    if (fieldsChanged && fieldsChanged.length > 0) {
      return (
        <div className="space-y-2">
          <h4 className="font-medium text-sm text-gray-700">Changed Fields:</h4>
          {fieldsChanged.map((field, index) => {
            const beforeValue = before && before[field];
            const afterValue = after && after[field];
            
            return (
              <div key={index} className="text-sm">
                <span className="font-medium text-gray-600">{field}:</span>
                {beforeValue !== undefined && afterValue !== undefined ? (
                  <div className="ml-4">
                    <div className="text-red-600">
                      <span className="font-mono">-</span> {String(beforeValue)}
                    </div>
                    <div className="text-green-600">
                      <span className="font-mono">+</span> {String(afterValue)}
                    </div>
                  </div>
                ) : afterValue !== undefined ? (
                  <div className="ml-4 text-green-600">
                    <span className="font-mono">+</span> {String(afterValue)}
                  </div>
                ) : (
                  <div className="ml-4 text-gray-500">Changed</div>
                )}
              </div>
            );
          })}
        </div>
      );
    }

    return null;
  };

  // PII masking functions
  const maskSensitiveData = (text) => {
    if (!text || showRawData) return text;
    return maskAllPII(String(text), { maskingLevel });
  };

  const maskLogField = (fieldName, value) => {
    if (!value || showRawData) return value;
    
    // Always mask certain sensitive fields
    const sensitivFields = ['email', 'phone', 'ssn', 'creditCard', 'password'];
    const shouldMask = sensitivFields.some(field => 
      fieldName.toLowerCase().includes(field)
    );
    
    if (shouldMask || maskingLevel !== MASKING_LEVELS.NONE) {
      return maskAllPII(String(value), { maskingLevel });
    }
    
    return value;
  };

  const getPIIIndicator = (text) => {
    if (!text) return null;
    const detection = detectPII(String(text));
    if (detection.hasPII) {
      const types = detection.types.map(t => t.type).join(', ');
      return (
        <span 
          className="inline-flex items-center px-2 py-1 text-xs bg-yellow-100 text-yellow-800 rounded-full ml-2"
          title={`Contains PII: ${types}`}
        >
          PII
        </span>
      );
    }
    return null;
  };

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-4 mb-2 hover:shadow-md transition-shadow">
      <div className="flex items-start justify-between">
        <div className="flex items-start space-x-3 flex-1">
          {/* Severity Icon */}
          <div className="flex-shrink-0 mt-1">
            {getSeverityIcon(log.severity)}
          </div>

          {/* Main Content */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center space-x-2 mb-1">
              <span className="text-lg">{getActionIcon(log.action)}</span>
              <span className="font-medium text-gray-900">{log.action}</span>
              <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${getSeverityBadgeColor(log.severity)}`}>
                {log.severity || 'LOW'}
              </span>
            </div>

            <p className="text-gray-700 text-sm mb-2">
              {formatDescription(log)}
            </p>

            <div className="flex items-center space-x-4 text-xs text-gray-500">
              <div className="flex items-center space-x-1">
                <User className="h-3 w-3" />
                <span>{maskSensitiveData(formatUserInfo(log))}</span>
                {getPIIIndicator(formatUserInfo(log))}
              </div>
              <div className="flex items-center space-x-1">
                <Clock className="h-3 w-3" />
                <span>{formatTimestamp(log.timestamp)}</span>
              </div>
              {log.entityType && (
                <div>
                  <span className="font-medium">Resource:</span> {log.entityType}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* PII and Controls */}
        <div className="flex items-center space-x-2">
          {/* PII Indicator */}
          {log.details && getPIIIndicator(JSON.stringify(log.details))}
          
          {/* PII Toggle Button */}
          <button
            onClick={() => setShowRawData(!showRawData)}
            className="flex-shrink-0 p-1 text-gray-400 hover:text-gray-600 transition-colors"
            title={showRawData ? "Hide sensitive data" : "Show raw data"}
          >
            {showRawData ? (
              <EyeOff className="h-4 w-4" />
            ) : (
              <Eye className="h-4 w-4" />
            )}
          </button>

          {/* Expand/Collapse Button */}
          {(log.changes || log.details || log.metadata) && (
            <button
              onClick={toggleExpanded}
              className="flex-shrink-0 p-1 text-gray-400 hover:text-gray-600 transition-colors"
            >
              {isExpanded ? (
                <ChevronDown className="h-4 w-4" />
              ) : (
                <ChevronRight className="h-4 w-4" />
              )}
            </button>
          )}
        </div>
      </div>

      {/* Expanded Details */}
      {isExpanded && (
        <div className="mt-4 pt-4 border-t border-gray-100 space-y-4">
          {/* Changes */}
          {log.changes && (
            <div>
              {formatChanges(log.changes)}
            </div>
          )}

          {/* Details */}
          {log.details && (
            <div>
              <h4 className="font-medium text-sm text-gray-700 mb-2">Details:</h4>
              <pre className="text-xs bg-gray-50 p-2 rounded border overflow-x-auto">
                {typeof log.details === 'string' 
                  ? maskSensitiveData(log.details)
                  : maskSensitiveData(JSON.stringify(log.details, null, 2))
                }
              </pre>
            </div>
          )}

          {/* Metadata */}
          {log.metadata && (
            <div>
              <h4 className="font-medium text-sm text-gray-700 mb-2">Metadata:</h4>
              <div className="text-xs space-y-1">
                {log.metadata.userAgent && (
                  <div>
                    <span className="font-medium">User Agent:</span> {maskLogField('userAgent', log.metadata.userAgent)}
                    {getPIIIndicator(log.metadata.userAgent)}
                  </div>
                )}
                {log.metadata.ipAddress && (
                  <div>
                    <span className="font-medium">IP Address:</span> {maskLogField('ipAddress', log.metadata.ipAddress)}
                    {getPIIIndicator(log.metadata.ipAddress)}
                  </div>
                )}
                {log.metadata.source && (
                  <div><span className="font-medium">Source:</span> {log.metadata.source}</div>
                )}
                {log.metadata.sessionId && (
                  <div><span className="font-medium">Session ID:</span> {log.metadata.sessionId}</div>
                )}
              </div>
            </div>
          )}

          {/* Raw Log Data (for debugging) */}
          {process.env.NODE_ENV === 'development' && (
            <details className="text-xs">
              <summary className="cursor-pointer font-medium text-gray-700">Raw Log Data</summary>
              <pre className="mt-2 bg-gray-50 p-2 rounded border overflow-x-auto">
                {JSON.stringify(log, null, 2)}
              </pre>
            </details>
          )}
        </div>
      )}
    </div>
  );
};

export default LogEntry;
